import { useCallback } from 'react';

import { IconX } from '@tabler/icons-react';
import clsx from 'clsx';
import Select, {
  OptionProps,
  InputProps,
  components,
  MenuProps,
  ControlProps,
  Props as SelectProps,
  SingleValue,
  MultiValue,
  ActionMeta,
} from 'react-select';

import { PillButton } from 'ui-kit/Buttons';

export type PillButtonComboboxProps<T, Multi extends boolean> = {
  options: T[];
  value?: T | T[];
  manuLabel?: string;
  comboLabel?: string;
  inputPlaceholder?: string;
  onClose?: () => void;
  onValueSelected: Exclude<SelectProps<T>['onChange'], undefined>;
  getOptionLabel?: SelectProps<T>['getOptionLabel'];
  getOptionValue?: SelectProps<T>['getOptionValue'];
  filterOption?: SelectProps<T>['filterOption'];
  isMulti?: Multi;
  // Stretch to the floating wrapper's width instead of shrinking to content.
  // Only meaningful paired with usePopout's matchReferenceWidth, which sets
  // that wrapper's width to the trigger pill's — together they make the
  // dropdown exactly as wide as the pill that opened it. Off by default:
  // the "Add ..." combobox variants are triggered by a small icon button,
  // not a pill, and need their own comfortable reading width regardless of
  // the trigger's size.
  fullWidth?: boolean;
};

export function PillButtonCombobox<T extends object, Multi extends boolean>(
  props: PillButtonComboboxProps<T, Multi>,
) {
  const {
    onValueSelected,
    onClose,
    comboLabel,
    manuLabel,
    inputPlaceholder,
    value,
    fullWidth,
    ...selectProps
  } = props;

  const handleValueChange = useCallback(
    (value: SingleValue<T> | MultiValue<T>, action: ActionMeta<T>) => {
      if (!value) {
        return;
      }
      onValueSelected(value, action);
      if (!selectProps.isMulti) {
        onClose?.();
      }
    },
    [onValueSelected, onClose, selectProps.isMulti],
  );

  const boundControl = useCallback(
    (props: ControlProps<T, Multi>) => (
      <Control<T, Multi> {...props} label={comboLabel} onCloseBtnClick={onClose} />
    ),
    [comboLabel, onClose],
  );
  const boundInput = useCallback(
    (props: InputProps<T, Multi>) => (
      <ComboInput<T, Multi> {...props} inputPlaceholder={inputPlaceholder} />
    ),
    [inputPlaceholder],
  );
  const boundMenu = useCallback(
    (props: MenuProps<T, Multi>) => <Menu<T, Multi> {...props} label={manuLabel} />,
    [manuLabel],
  );
  const boundOption = useCallback(
    (props: OptionProps<T, Multi>) => (
      <OptionComp<T, Multi> {...props} onValueSelected={handleValueChange} />
    ),
    [handleValueChange],
  );
  const boundNoOptions = useCallback(
    () => <div className="mt-4 text-xs leading-[1.67] text-white">No results found</div>,
    [],
  );

  return (
    <Select<T, Multi>
      {...selectProps}
      // Controlled, not defaultValue: OptionComp below bypasses
      // react-select's own selection pipeline entirely (it computes
      // newValue itself and calls onValueSelected directly, never
      // going through react-select's internal useStateManager setValue
      // path), so with defaultValue react-select's own internal value
      // never advances past whatever it was at mount -- every option
      // click on a multi-select reads that same frozen initial value
      // back out of props.selectProps.value, so accumulating
      // [...currentArray, newOption] only ever adds one option to the
      // *original* selection, silently dropping every earlier pick
      // from the same open session. Passing the caller's live value as
      // a true controlled prop keeps selectProps.value in sync with
      // what was actually last selected.
      value={value}
      unstyled={true}
      onChange={handleValueChange}
      className={clsx(
        // border-gray-700/bg-gray-950 match every pill's own border/surface
        // color (was border-pink-500 — a full 1px frame around the whole
        // panel, much heavier than how pink is used everywhere else in the
        // app: a hairline accent on isActive/focus states, not a panel
        // border. Reads as a native extension of the pill it opened from
        // now, not a different, bolted-on component.
        'mt-1 rounded border border-gray-700 bg-gray-950 px-3 shadow-[0px_8px_16px_0px_rgba(13,13,13,0.9)]',
        'py-4',
        fullWidth ? 'w-full' : 'w-fit',
      )}
      menuIsOpen={true}
      maxMenuHeight={180}
      components={{
        Control: boundControl,
        Input: boundInput,
        Option: boundOption,
        DropdownIndicator: () => null,
        // react-select renders this by default to divide the input area from the dropdown
        // arrow; DropdownIndicator is already nulled above, so this separator has nothing left
        // to separate.
        IndicatorSeparator: () => null,
        Menu: boundMenu,
        Placeholder: () => null,
        NoOptionsMessage: boundNoOptions,
      }}
      styles={{
        menu: () => ({}),
        valueContainer: (base) => ({
          ...base,
          padding: 4,
        }),
      }}
      controlShouldRenderValue={false}
      backspaceRemovesValue={false}
      isClearable={false}
      escapeClearsValue={true}
      hideSelectedOptions={!selectProps.isMulti}
      tabSelectsValue={false}
    />
  );
}

function OptionComp<T, Multi extends boolean>(
  props: OptionProps<T, Multi> & {
    onValueSelected: Exclude<SelectProps<T>['onChange'], undefined>;
  },
) {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const currentValue = props.selectProps.value;
    const getOptionValue = props.selectProps.getOptionValue || ((option: T) => String(option));
    const optionValue = getOptionValue(props.data);
    let newValue: SingleValue<T> | MultiValue<T>;
    let action: ActionMeta<T>;

    if (props.isSelected) {
      if (props.selectProps.isMulti) {
        const currentArray = (currentValue as MultiValue<T>) || [];
        newValue = currentArray.filter(
          (item) => getOptionValue(item) !== optionValue,
        ) as MultiValue<T>;
      } else {
        newValue = null as SingleValue<T>;
      }
      action = {
        action: 'deselect-option',
        option: props.data,
        removedValue: props.data,
        name: props.selectProps.name,
      };
    } else {
      if (props.selectProps.isMulti) {
        const currentArray = (currentValue as MultiValue<T>) || [];
        newValue = [...currentArray, props.data] as MultiValue<T>;
      } else {
        newValue = props.data as SingleValue<T>;
      }
      action = {
        action: 'select-option',
        option: props.data,
        name: props.selectProps.name,
      };
    }

    // Only fire the select/deselect through this manual computation — it
    // already fully replicates what react-select's own onClick would do
    // (and is the only thing that can express "click a selected option to
    // deselect it," which a plain click hand-off doesn't support). Also
    // invoking props.innerProps.onClick(e) here used to additionally run
    // react-select's native click-to-select path on the same click, so
    // Select's onChange (and everything wired to it, e.g. a caller's
    // functional setState) fired twice per click — visible as "picking one
    // option adds two" wherever the caller accumulates via a functional
    // updater instead of a plain array literal.
    props.onValueSelected(newValue, action);
  };

  const handleIconClick = () => {
    const syntheticEvent = {
      stopPropagation: () => {},
      preventDefault: () => {},
    } as React.MouseEvent<HTMLDivElement>;
    handleClick(syntheticEvent);
  };

  return (
    <div
      ref={props.innerRef}
      {...props.innerProps}
      className="mt-4"
      role="option"
      aria-selected={props.isSelected}
      onClick={handleClick}
    >
      <PillButton
        // Full width + justify-between: each option row fills the flyout
        // (was auto-width, so shorter labels left a ragged gap of empty
        // panel to their right instead of a clean list) with its +/x icon
        // pinned to the trailing edge instead of sitting right after the
        // label text.
        className="w-full justify-between"
        isActive={props.isSelected}
        isFocused={props.isFocused}
        iconName={props.isSelected ? 'pill-close' : 'pill-plus'}
        description={props.label}
        iconOnClick={handleIconClick}
        showIconAlways={true}
      />
    </div>
  );
}

function ComboInput<T, Multi extends boolean>(
  props: InputProps<T, Multi> & { inputPlaceholder?: string },
) {
  const {
    inputPlaceholder,
    capture: _capture,
    clearValue: _clearValue,
    getClassNames: _getClassNames,
    cx: _cx,
    getStyles: _getStyles,
    getValue: _getValue,
    hasValue: _hasValue,
    isHidden: _isHidden,
    isMulti: _isMulti,
    isRtl: _isRtl,
    selectOption: _selectOption,
    selectProps: _selectProps,
    setValue: _setValue,
    innerRef: _innerRef,
    inputClassName: _inputClassName,
    isDisabled: _isDisabled,
    ...inputProps
  } = props;
  return (
    <input
      ref={props.innerRef}
      placeholder={inputPlaceholder}
      autoFocus
      {...inputProps}
      // Was bg-black/border-[#868e9f] — the only bg-black surface in the
      // whole flow; every pill and panel elsewhere is bg-gray-950. Matches
      // the panel it sits in now.
      className="mt-1 w-full rounded border border-gray-700 bg-gray-950 px-4 py-3 text-white outline-none"
    />
  );
}

function Menu<T, Multi extends boolean>(props: MenuProps<T, Multi> & { label?: string }) {
  return (
    <>
      {props.label ? (
        <div className="mt-3 text-xs leading-[1.67] text-slate-200">{props.label}</div>
      ) : null}
      <components.Menu {...props}>{props.children}</components.Menu>
    </>
  );
}

function Control<T, Multi extends boolean>(
  props: ControlProps<T, Multi> & {
    label?: string;
    onCloseBtnClick?: () => void;
  },
) {
  return (
    <>
      {props.label || props.onCloseBtnClick ? (
        <div className="flex items-center justify-between">
          <span className="text-xs text-white">{props.label}</span>
          <button
            aria-label="Close"
            className="border-none bg-transparent hover:bg-slate-700 active:bg-slate-700"
            onClick={props.onCloseBtnClick}
            tabIndex={0}
          >
            <IconX color="white" size={20} />
          </button>
        </div>
      ) : null}
      <components.Control {...props}>{props.children}</components.Control>
    </>
  );
}
